/**
 * The stock screen's rules and its words.
 *
 * The rules are `controllers/client/stockFunctions.js` REASONS, restated so
 * the form can refuse what the server would refuse — before a delivery is
 * typed out rather than after. If the two ever disagree the server wins; it is
 * the one that writes.
 *
 * The words are the phone's, taken from `stock_screen.dart`,
 * `stock_entry_sheet.dart` and `stock_item_screen.dart`. Somali where the
 * phone says Somali, English everywhere else, and nothing invented that the
 * phone already has a word for.
 */

/**
 * Which reasons belong to which direction, and what each one demands.
 *
 *   stationRequired  — the server throws without it
 *   vendorRequired   — same
 *   vendorOnCredit   — a purchase on credit needs the supplier it is owed to
 *   noteRequired     — so a waste or an adjustment is never unexplained
 *
 * `Adjustment` appears on both sides deliberately: it is ONE wire value, and
 * which direction it is comes from `type`, not from the reason.
 */
export const REASONS = {
  in: [
    { value: 'Purchase', label: 'Purchase', vendorOnCredit: true, pays: true },
    { value: 'Opening', label: 'Opening' },
    { value: 'Adjustment', label: 'Adjustment', noteRequired: true },
  ],
  out: [
    { value: 'Usage', label: 'Usage', stationRequired: true },
    { value: 'Waste', label: 'Waste', noteRequired: true },
    { value: 'Return', label: 'Return', vendorRequired: true },
    { value: 'Adjustment', label: 'Adjustment', noteRequired: true },
  ],
};

export const reasonRules = (type, reason) =>
  (REASONS[type] || []).find((entry) => entry.value === reason) || null;

/** The fallback list in `stockController.assertUnit`, for a business with none. */
export const DEFAULT_UNITS = ['kg', 'g', 'L', 'ml', 'pcs', 'box', 'sack', 'crate', 'bottle'];

/** Manager, storekeeper and superadmin write; an accountant only reads. */
export const READ_ROLES = ['manager', 'storekeeper', 'accountant', 'superadmin-superadmin'];
export const WRITE_ROLES = ['manager', 'storekeeper', 'superadmin-superadmin'];

export const COPY = {
  /* the screen */
  title: 'Stock',
  items: 'Items',
  movementsTab: 'Movements',
  searchStock: 'Search stock',
  searchMovements: 'Number, vendor, station or item',
  allStock: 'All stock',
  low: 'Low',
  outOfStock: 'Out of stock',
  outBadge: 'Out',
  lowBadge: 'Low',
  newItem: 'New stock item',
  stockOnHand: 'STOCK ON HAND',
  stockValue: 'Stock value',
  itemsCount: (n) => `${n} item${n === 1 ? '' : 's'}`,

  /* the two buttons everything starts from */
  stockIn: 'Stock In',
  stockOut: 'Stock Out',

  /* the feed */
  movements: 'Stock Movements',
  received: 'Received',
  used: 'Used',
  wasted: 'Wasted',
  returned: 'Returned',
  lines: 'lines',
  today: 'Today',
  yesterday: 'Yesterday',
  canceled: 'Canceled',
  allStations: 'All stations',
  noMovements: 'No movements in this period.',
  // The word under each card's total, which the phone picks the same way.
  trailing: (movement) => {
    if (movement.type === 'in') return 'received';
    if (movement.reason === 'Waste') return 'lost';
    if (movement.reason === 'Return') return 'returned';
    return 'used';
  },

  /* the empty store */
  nothingYet: 'Nothing in the store yet',
  nothingYetBody:
    'Add your items first, then load what is already on the shelf with a Stock In using the Opening reason.',
  addFirst: 'Add the first item',

  /* the entry sheet */
  reason: 'Reason',
  payment: 'Payment',
  cash: 'Cash',
  onCredit: 'On credit',
  // The phone's label for the supplier, in Somali, on an otherwise English sheet.
  vendor: 'Shirkadda',
  vendorOptional: '— optional on a cash buy',
  station: 'Station',
  invoiceNote: 'Invoice no. / note',
  note: 'Note',
  date: 'Date',
  required: 'required',
  invoiceNoteHint: 'e.g. 88213',
  outNoteHint: 'What happened?',
  addItems: 'Kusoo Dar Alaab',
  noItemsYet: 'No items yet',
  quantity: 'Quantity',
  costPer: (unit) => `Cost per ${unit}`,
  onHand: (qty, unit) => `${qty} ${unit} on hand`,
  lineTotal: 'Line total',
  leftAfter: 'Left after',
  onlyOnHand: (qty, unit) => `Only ${qty} ${unit} on hand`,
  postTogether: (n) => `All ${n} lines post together.`,
  invoiceTotal: 'INVOICE TOTAL',
  valueOut: 'VALUE OUT',
  valueLost: 'VALUE LOST',

  /* what stops a save, in the phone's words */
  noLines: 'soo dooro ugu alaab.',
  noQuantity: (n) => `${n} line${n === 1 ? ' has' : 's have'} no quantity yet.`,
  overIssue: 'Some lines take out more than the store has.',
  needVendorCredit: 'Choose the vendor it is owed to.',
  needStation: 'Choose the station.',
  needVendorReturn: 'Choose the vendor it goes back to.',
  needNote: 'Write what happened.',
  noStations:
    'No stations set up yet. Add one under Stations first — usage has to be posted somewhere.',

  /** What the save button says, which is different for every reason. */
  submitLabel: ({ type, reason, stationName, editingNo }) => {
    if (editingNo) return `Update ${editingNo}`;
    if (type === 'out') {
      if (reason === 'Waste') return 'Record waste';
      if (reason === 'Return') return 'Return to vendor';
      if (reason === 'Usage') return stationName ? `Post to ${stationName}` : 'Post usage';
      return 'Save adjustment';
    }
    if (reason === 'Opening') return 'Save opening';
    if (reason === 'Adjustment') return 'Save adjustment';
    return 'Save purchase';
  },

  /* the picker */
  pickerTitle: 'Add items',
  pickerAdded: (n) => `${n} added`,
  pickerDone: 'Done',
  pickerAdd: 'Add',
  pickerCost: 'Cost',
  nothingCalled: (query) => `Nothing called "${query}".`,
  createIt: 'Create it',

  /* one item */
  itemOnHand: 'On hand',
  itemValue: 'Value',
  itemReceived: 'Received',
  itemUsedWasted: 'Used / wasted',
  historyTitle: 'Dhaqdhaqaaqa alaabta',
  historyEmpty: 'Majiraan wax dhaqdhaqaaq ah oo alaabtaan ku saabsan.',
  leftQty: (qty, unit) => `left ${qty} ${unit}`,
  editItem: 'Edit item',
  removeItem: 'Remove item',

  /* the item form */
  newItemTitle: 'New stock item',
  editItemTitle: 'Edit stock item',
  nameLabel: 'Name',
  nameHint: 'e.g. Rice Basmati',
  nameMissing: 'Enter an item name',
  unitLabel: 'Unit',
  costLabel: 'Cost',
  reorderLabel: 'Low stock at',
  reorderHint: 'e.g. 10',
  addItem: 'Add stock item',
  saveChanges: 'Save changes',
  // Why the two most interesting fields are missing or locked.
  quantityIsHistory:
    'Quantity only moves through Stock In and Stock Out, so what is on the shelf always matches the history.',
  costIsPurchases:
    'Cost follows the purchases. Once there is stock on the shelf, post a Stock In at the new price instead.',

  /* removing an item */
  deleteTitle: (name) => `Delete ${name}?`,
  archiveTitle: (name) => `Archive ${name}?`,
  deleteBody: 'Alaabti la tiro dib looma soo celin karo, MA HUBTAA?',
  archiveNote: (n) =>
    `It appears on ${n} movement${n === 1 ? '' : 's'}, so it is archived instead of deleted and its history is kept.`,
  delete: 'Delete',
  cancel: 'Cancel',

  /* cancelling a movement */
  cancelTitle: (no) => `Cancel ${no}?`,
  cancelInBody: 'What this delivery brought in comes back off the shelf.',
  cancelOutBody:
    'What this took out goes back on the shelf, and the expense it raised is cancelled.',
  cancelHint: 'Why? (kept on the record)',
  keepIt: 'Keep it',
  cancelIt: 'Cancel it',

  /* the detail rows */
  vendorRow: 'Vendor',
  stationRow: 'Station',
  paymentRow: 'Payment',
  noteRow: 'Note',
  enteredBy: 'Entered by',
  canceledBy: 'Canceled by',
  cancelReason: 'Cancel reason',
  edit: 'Edit',

  notAllowed: 'Only a manager or a storekeeper can open Stock.',
  readOnly: 'You can read the store, but not change it.',
};
